'use client';

/**
 * DataTable Component
 *
 * Comprehensive, reusable data table with Card wrapper.
 * Supports dynamic columns, sorting, filtering, pagination, selection,
 * virtualization, accessibility, theming, and more.
 *
 * @example
 * <DataTable
 *   data={mailboxes}
 *   rowKey="id"
 *   columns={columns}
 *   selectionMode="multiple"
 *   bulkActions={bulkActions}
 *   pagination={{ mode: 'client', pageSize: 10 }}
 * />
 */

import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';
import {
  DataTableProps,
  SelectionState,
  SortState,
  FilterState,
  PaginationState,
  defaultI18n,
} from './types';
import { DataTableThemeProvider, useDataTableTheme } from './theme/ThemeProvider';
import { themeClasses, getTextStyles } from './theme/tokens';
import { TableCell } from './TableCell';
import { LoadingState } from './LoadingState';
import { EmptyState } from './EmptyState';
import { ErrorState } from './ErrorState';
import { BulkActionsToolbar } from './BulkActionsToolbar';
import { sortData, toggleSortDirection } from './utils/sorting';
import { filterData } from './utils/filtering';
import { emitAnalytics } from './utils/analytics';
import { ArrowUp, ArrowDown, ArrowUpDown } from 'lucide-react';
import {
  HORIZONTAL_SCROLL_GUARD_CLASS,
  attachHorizontalScrollGuard,
} from '../horizontalScrollGuard';

/**
 * Main DataTable component (inner, expects theme context)
 */
function DataTableInner<TData = any>(props: DataTableProps<TData>) {
  const {
    data,
    rowKey,
    columns,
    bulkActions = [],
    selectionMode = 'none',
    initialState,
    pagination,
    density = 'comfortable',
    layout = 'fluid',
    stickyHeader = true,
    stickyActions = true,
    bordered = false,
    loading,
    empty,
    error,
    callbacks,
    analytics,
    className,
    ariaLabel,
    getRowClass,
    i18n = {},
    rtl = false,
    expandable,
    clearSelectionTrigger,
    crossPageSelection,
    minWidth,
  } = props;

  // GH-1855: cross-page selection — accessor for the stable row id used to
  // track exclusions across pages. Defaults to the table's rowKey.
  const getCrossPageId = useCallback(
    (row: TData): string | number => {
      const id = crossPageSelection?.rowId ?? rowKey;
      return typeof id === 'function'
        ? (id as (row: TData) => string | number)(row)
        : (row[id as keyof TData] as string | number);
    },
    [crossPageSelection?.rowId, rowKey]
  );

  const { cssVars, theme } = useDataTableTheme();
  const strings = { ...defaultI18n, ...i18n };

  // When a px floor is set, express it as `max(100%, Npx)` so the table still
  // fills the container (never leaves blank space on the right) but refuses to
  // shrink below the floor — at which point the wrapper's overflow-x scrolls.
  // A bare `min-width: Npx` would clobber the `min-w-full` class and let the
  // table render narrower than its container.
  const tableMinWidthStyle = minWidth
    ? { minWidth: `max(100%, ${minWidth}px)` }
    : undefined;

  // Get typography styles (inline styles to avoid Tailwind arbitrary value conflicts)
  const textStyles = getTextStyles(theme);

  // ============================================================================
  // State Management
  // ============================================================================

  const [sortState, setSortState] = useState<SortState | null>(
    initialState?.sort || null
  );
  const [filters] = useState<FilterState[]>(
    initialState?.filters || []
  );
  const [selection, setSelection] = useState<SelectionState>({
    selectedIndices: [],
    allSelected: false,
    allSelectedAcrossPages: false,
    excludedIds: [],
  });

  // Reset internal selection when parent signals a clear (e.g. after HubSpot sync)
  useEffect(() => {
    if (clearSelectionTrigger === undefined) return;
    setSelection({ selectedIndices: [], allSelected: false, allSelectedAcrossPages: false, excludedIds: [] });
  }, [clearSelectionTrigger]);
  const [paginationState, setPaginationState] = useState<PaginationState>(
    pagination
      ? {
          page: initialState?.pagination?.page || 0,
          pageSize: pagination.pageSize || initialState?.pagination?.pageSize || 10,
          total: pagination.total,
        }
      : { page: 0, pageSize: data.length }
  );

  const [expandedRowKeys, setExpandedRowKeys] = useState<Set<string | number>>(
    new Set(expandable?.defaultExpandedRows || [])
  );

  // Track if table has horizontal scroll (for conditional sticky action columns)
  const [hasHorizontalScroll, setHasHorizontalScroll] = useState(false);
  const scrollContainerRef = React.useRef<HTMLDivElement>(null);

  // Block macOS trackpad horizontal overscroll from triggering browser back/forward
  // navigation at the table's horizontal edges. React's synthetic onWheel is passive
  // (preventDefault is a no-op there), so attach a real non-passive listener. Pairs
  // with the `overscroll-x-none` CSS on the scroll container. Shared with HubTable.
  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;
    return attachHorizontalScrollGuard(el);
  }, []);

  // ============================================================================
  // Row Key Accessor
  // ============================================================================

  const getRowKey = useCallback(
    (row: TData, index: number): string => {
      const baseKey = typeof rowKey === 'function'
        ? rowKey(row)
        : (row[rowKey] as string | number);
      return `${baseKey}-${index}`;
    },
    [rowKey]
  );

  // ============================================================================
  // Data Processing (Sort → Filter → Paginate)
  // ============================================================================

  const processedData = useMemo(() => {
    let result = [...data];

    // 1. Sort (client-side only - server-side sorting handled by parent)
    if (sortState && pagination?.mode === 'client') {
      const sortColumn = columns.find((col) => col.id === sortState.columnId);
      if (sortColumn) {
        result = sortData(result, sortColumn, sortState.direction);
      }
    }

    // 2. Filter (client-side only - server-side filtering handled by parent)
    if (filters.length > 0 && pagination?.mode === 'client') {
      result = filterData(result, filters, columns);
    }

    // 3. Paginate (client-side only - server-side pagination handled by parent)
    if (pagination?.mode === 'client') {
      const start = paginationState.page * paginationState.pageSize;
      const end = start + paginationState.pageSize;
      result = result.slice(start, end);
    }

    return result;
  }, [data, sortState, filters, paginationState, pagination?.mode, columns]);

  // Update pagination total for client mode
  useEffect(() => {
    if (pagination?.mode === 'client') {
      setPaginationState((prev) => ({ ...prev, total: data.length }));
    }
  }, [data.length, pagination?.mode]);

  // ============================================================================
  // Sorting Handlers
  // ============================================================================

  const handleSort = useCallback(
    (columnId: string) => {
      const column = columns.find((col) => col.id === columnId);
      if (!column?.sortable) return;

      setSortState((current) => {
        const newDirection =
          current?.columnId === columnId
            ? toggleSortDirection(current.direction, column.defaultSortDirection)
            : column.defaultSortDirection ?? 'asc';

        const newSort = newDirection
          ? { columnId, direction: newDirection }
          : null;

        // Emit analytics
        if (newSort) {
          emitAnalytics(analytics, 'table.sort', {
            columnId,
            direction: newDirection,
          });
        }

        // Callback
        callbacks?.onSortChange?.(newSort);

        return newSort;
      });
    },
    [columns, analytics, callbacks]
  );

  // ============================================================================
  // Selection Handlers
  // ============================================================================

  const handleSelectAll = useCallback(() => {
    let nextState: SelectionState = { selectedIndices: [], allSelected: false };

    setSelection((prev) => {
      // In cross-page mode, the header checkbox is a 3-state control:
      //   - any-on-page selected  → click clears (page + cross-page) entirely
      //   - none-on-page selected → click selects current page only (banner appears separately)
      // The "Select all N across pages" affordance lives in the banner, NOT
      // in this checkbox, so we never flip into cross-page mode from here.
      if (prev.allSelectedAcrossPages || prev.selectedIndices.length > 0) {
        nextState = { selectedIndices: [], allSelected: false, allSelectedAcrossPages: false, excludedIds: [] };
      } else {
        const indices = processedData.map((_, idx) => idx);
        nextState = { selectedIndices: indices, allSelected: indices.length > 0, allSelectedAcrossPages: false, excludedIds: [] };
      }
      return nextState;
    });

    emitAnalytics(analytics, 'table.select', {
      count: nextState.selectedIndices.length,
      mode: 'all',
    });

    callbacks?.onSelectionChange?.(nextState);
  }, [processedData, analytics, callbacks]);

  // GH-1855: user opts in to "select all N across pages" via inline banner.
  const handleSelectAllAcrossPages = useCallback(() => {
    const next: SelectionState = {
      selectedIndices: processedData.map((_, idx) => idx),
      allSelected: true,
      allSelectedAcrossPages: true,
      excludedIds: [],
    };
    setSelection(next);
    emitAnalytics(analytics, 'table.select', {
      count: crossPageSelection?.total ?? processedData.length,
      mode: 'all-across-pages',
    });
    callbacks?.onSelectionChange?.(next);
  }, [processedData, analytics, callbacks, crossPageSelection?.total]);

  const handleSelectRow = useCallback(
    (index: number) => {
      let snapshot: SelectionState = { selectedIndices: [], allSelected: false };
      setSelection((prev) => {
        // Cross-page mode: unchecking a row adds it to excludedIds. The
        // visible selectedIndices stay in sync with what's NOT excluded on
        // the current page so the row checkbox UI reflects state correctly.
        if (prev.allSelectedAcrossPages) {
          const row = processedData[index];
          if (!row) return prev;
          const rowId = getCrossPageId(row);
          const excluded = new Set(prev.excludedIds || []);
          const isSelected = prev.selectedIndices.includes(index);
          if (isSelected) {
            excluded.add(rowId);
          } else {
            excluded.delete(rowId);
          }
          const nextIndices = processedData
            .map((r, i) => (excluded.has(getCrossPageId(r)) ? -1 : i))
            .filter((i) => i >= 0);
          snapshot = {
            selectedIndices: nextIndices,
            allSelected: nextIndices.length === processedData.length && processedData.length > 0,
            allSelectedAcrossPages: true,
            excludedIds: Array.from(excluded),
          };
          return snapshot;
        }

        const isSelected = prev.selectedIndices.includes(index);
        const nextIndices = isSelected
          ? prev.selectedIndices.filter((i) => i !== index)
          : selectionMode === 'single'
          ? [index]
          : [...prev.selectedIndices, index];

        snapshot = {
          selectedIndices: nextIndices,
          allSelected: nextIndices.length === processedData.length && processedData.length > 0,
          allSelectedAcrossPages: false,
          excludedIds: [],
        };
        return snapshot;
      });

      emitAnalytics(analytics, 'table.select', {
        count: snapshot.selectedIndices.length,
        mode: selectionMode,
      });

      callbacks?.onSelectionChange?.(snapshot);
    },
    [selectionMode, processedData, analytics, callbacks, getCrossPageId]
  );

  const handleClearSelection = useCallback(() => {
    const cleared: SelectionState = { selectedIndices: [], allSelected: false, allSelectedAcrossPages: false, excludedIds: [] };
    setSelection(cleared);
    callbacks?.onSelectionChange?.(cleared);
  }, [callbacks]);

  // GH-1855: when the underlying page data changes (server pagination,
  // search, etc.), recompute which indices on the new page are selected so
  // the row checkboxes mirror cross-page state correctly.
  useEffect(() => {
    if (!selection.allSelectedAcrossPages) return;
    const excluded = new Set(selection.excludedIds || []);
    const nextIndices = processedData
      .map((r, i) => (excluded.has(getCrossPageId(r)) ? -1 : i))
      .filter((i) => i >= 0);
    setSelection((prev) => {
      if (!prev.allSelectedAcrossPages) return prev;
      if (
        prev.selectedIndices.length === nextIndices.length &&
        prev.selectedIndices.every((v, i) => v === nextIndices[i])
      ) {
        return prev;
      }
      const next = {
        ...prev,
        selectedIndices: nextIndices,
        allSelected: nextIndices.length === processedData.length && processedData.length > 0,
      };
      callbacks?.onSelectionChange?.(next);
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [processedData]);

  // ============================================================================
  // Expandable Row Handlers
  // ============================================================================

  const handleToggleExpand = useCallback(
    (rowKeyValue: string | number) => {
      setExpandedRowKeys((prev) => {
        const newSet = new Set(prev);
        if (newSet.has(rowKeyValue)) {
          newSet.delete(rowKeyValue);
        } else {
          newSet.add(rowKeyValue);
        }

        expandable?.onExpandChange?.(Array.from(newSet));
        return newSet;
      });
    },
    [expandable]
  );

  // ============================================================================
  // Render Helpers
  // ============================================================================

  const renderHeaderCell = (column: typeof columns[number]) => {
    const isSorted = sortState?.columnId === column.id;
    const sortDirection = isSorted ? sortState.direction : null;
    const sortIcon = column.sortable ? (
      isSorted ? (
        sortDirection === 'asc' ? (
          <ArrowUp className="h-3.5 w-3.5 text-slate-900" />
        ) : (
          <ArrowDown className="h-3.5 w-3.5 text-slate-900" />
        )
      ) : (
        <ArrowUpDown className="h-3.5 w-3.5 text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity" />
      )
    ) : null;

    // Determine justification based on column alignment
    const justifyClass = column.align === 'right'
      ? 'justify-end'
      : column.align === 'center'
      ? 'justify-center'
      : 'justify-start';

    if (column.renderHeader) {
      return (
        <div
          role={column.sortable ? 'button' : undefined}
          tabIndex={column.sortable ? 0 : undefined}
          className={cn(
            'flex items-center gap-2 w-full group',
            justifyClass,
            column.sortable && 'cursor-pointer hover:text-slate-900 transition-colors',
            themeClasses.focus
          )}
          style={textStyles.header()}
          onClick={() => column.sortable && handleSort(column.id)}
          onKeyDown={(event) => {
            if (!column.sortable || (event.key !== 'Enter' && event.key !== ' ')) return;
            event.preventDefault();
            handleSort(column.id);
          }}
          aria-label={
            isSorted
              ? `${column.label}, sorted ${sortDirection === 'asc' ? 'ascending' : 'descending'}`
              : column.sortable
              ? `${column.label}, sortable`
              : column.label
          }
        >
          <span className="min-w-0">{column.renderHeader(column)}</span>
          {sortIcon}
        </div>
      );
    }

    return (
      <button
        className={cn(
          'flex items-center gap-2 w-full group',
          justifyClass,
          column.sortable && 'cursor-pointer hover:text-slate-900 transition-colors',
          themeClasses.focus
        )}
        style={textStyles.header()}
        onClick={() => column.sortable && handleSort(column.id)}
        disabled={!column.sortable}
        aria-label={
          isSorted
            ? `${column.label}, sorted ${sortDirection === 'asc' ? 'ascending' : 'descending'}`
            : column.sortable
            ? `${column.label}, sortable`
            : column.label
        }
      >
        <span>{column.label}</span>
        {sortIcon}
      </button>
    );
  };

  // Determine row classes based on density
  const rowClass = density === 'comfortable'
    ? themeClasses.row.comfortable
    : themeClasses.row.compact;

  // ============================================================================
  // Render
  // ============================================================================

  const showCheckbox = selectionMode !== 'none';
  const visibleColumns = columns.filter((col) => !initialState?.columnVisibility || initialState.columnVisibility[col.id] !== false);
  const columnCount = visibleColumns.length + (showCheckbox ? 1 : 0);
  const tableLayoutClass = layout === 'fixed' ? 'table-fixed' : 'table-auto';

  // ============================================================================
  // Horizontal Scroll Detection
  // ============================================================================

  useEffect(() => {
    const checkHorizontalScroll = () => {
      if (scrollContainerRef.current) {
        const hasScroll = scrollContainerRef.current.scrollWidth > scrollContainerRef.current.clientWidth;
        setHasHorizontalScroll(hasScroll);
      }
    };

    // Check on mount and when data/columns change
    checkHorizontalScroll();

    // Use ResizeObserver to detect size changes
    const resizeObserver = new ResizeObserver(() => {
      checkHorizontalScroll();
    });

    if (scrollContainerRef.current) {
      resizeObserver.observe(scrollContainerRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [data, columns, visibleColumns]);

  // Loading state
  if (loading?.isLoading) {
    return (
      <Card className={cn(themeClasses.card, 'rounded-none border-0', className)} style={cssVars}>
        <CardContent className="p-0">
          <div className="overflow-x-auto max-w-full">
            <table className={cn(layout === 'fixed' ? 'w-full' : 'min-w-full', tableLayoutClass)} aria-label={ariaLabel || strings.table} style={tableMinWidthStyle}>
              <thead className={cn(themeClasses.tableHeader, stickyHeader && 'sticky top-0 z-30')}>
                <tr>
                  {showCheckbox && <th className={cn(themeClasses.headerCell, 'w-8')} />}
                  {visibleColumns.map((column) => (
                    <th
                      key={column.id}
                      className={cn(themeClasses.headerCell, 'text-left')}
                      style={textStyles.header()}
                    >
                      {column.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <LoadingState
                skeletonCount={loading.skeletonCount}
                columnCount={columnCount}
                message={loading.message}
                density={density}
              />
            </table>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Error state
  if (error?.hasError) {
    return (
      <Card className={cn(themeClasses.card, 'rounded-none border-0', className)} style={cssVars}>
        <CardContent className="p-0">
          <div className="overflow-x-auto max-w-full">
            <table className={cn(layout === 'fixed' ? 'w-full' : 'min-w-full', tableLayoutClass)} aria-label={ariaLabel || strings.table} style={tableMinWidthStyle}>
              <thead className={cn(themeClasses.tableHeader, stickyHeader && 'sticky top-0 z-30')}>
                <tr>
                  {showCheckbox && <th className={cn(themeClasses.headerCell, 'w-8')} />}
                  {visibleColumns.map((column) => (
                    <th
                      key={column.id}
                      className={cn(themeClasses.headerCell, 'text-left')}
                      style={textStyles.header()}
                    >
                      {column.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <ErrorState
                title={error.title}
                message={error.message}
                retryLabel={error.retryLabel}
                onRetry={error.onRetry}
                colSpan={columnCount}
              />
            </table>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Empty state
  if (processedData.length === 0) {
    return (
      <Card className={cn(themeClasses.card, 'rounded-none border-0', className)} style={cssVars}>
        <CardContent className="p-0">
          <div className="overflow-x-auto max-w-full">
            <table className={cn(layout === 'fixed' ? 'w-full' : 'min-w-full', tableLayoutClass)} aria-label={ariaLabel || strings.table} style={tableMinWidthStyle}>
              <thead className={cn(themeClasses.tableHeader, stickyHeader && 'sticky top-0 z-30')}>
                <tr>
                  {showCheckbox && <th className={cn(themeClasses.headerCell, 'w-8')} />}
                  {visibleColumns.map((column) => {
                    const isSorted = sortState?.columnId === column.id;
                    const sortDirection = isSorted ? sortState.direction : null;

                    return (
                      <th
                        key={column.id}
                        className={themeClasses.headerCell}
                        style={textStyles.header()}
                        aria-sort={
                          column.sortable && isSorted
                            ? sortDirection === 'asc'
                              ? 'ascending'
                              : 'descending'
                            : undefined
                        }
                      >
                        {renderHeaderCell(column)}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <EmptyState
                icon={empty?.icon}
                title={empty?.title}
                description={empty?.description}
                actionLabel={empty?.actionLabel}
                onAction={empty?.onAction}
                colSpan={columnCount}
              />
            </table>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Main table render
  const selectedRows = selection.selectedIndices.map((idx) => processedData[idx]);

  // Gridlines — just the outer card border here. Per-cell borders are applied
  // directly on each <th>/<td> (see below + TableCell) because descendant-
  // selector arbitrary variants (`[&_tbody_td]:border-b`) on the Card can be
  // overridden or silently dropped depending on how Tailwind reorders utility
  // layers — inline per-cell classes are deterministic.
  const borderedCardClass = bordered ? 'border border-slate-200' : '';

  return (
    <Card className={cn(themeClasses.card, 'rounded-none border-0', borderedCardClass, className)} style={cssVars} dir={rtl ? 'rtl' : 'ltr'}>
      <CardContent className="p-0 relative">
        {/* Background refetch indicator — thin animated bar at top of card */}
        {loading?.isFetching && !loading?.isLoading && (
          <div className="absolute top-0 left-0 right-0 h-0.5 z-40 overflow-hidden">
            <div
              className="h-full bg-blue-500 w-2/5"
              style={{ animation: 'loading-bar 1.4s ease-in-out infinite' }}
            />
          </div>
        )}
        {/* Bulk Actions Toolbar */}
        {selection.selectedIndices.length > 0 && bulkActions.length > 0 && (
          <BulkActionsToolbar
            selectedCount={selection.selectedIndices.length}
            selectedRows={selectedRows}
            selectedIndices={selection.selectedIndices}
            actions={bulkActions}
            onClearSelection={handleClearSelection}
            i18n={strings}
          />
        )}

        {/* GH-1855: cross-page selection banner. Shows once the current page
            is fully selected AND there are more rows across other pages. */}
        {crossPageSelection?.enabled &&
          processedData.length > 0 &&
          selection.allSelected &&
          !selection.allSelectedAcrossPages &&
          crossPageSelection.total > processedData.length && (
            <div className="flex items-center justify-center gap-2 border-b border-blue-100 bg-blue-50/70 px-4 py-2 text-[13px] text-slate-700">
              <span>
                All {processedData.length} on this page selected.
              </span>
              <button
                type="button"
                onClick={handleSelectAllAcrossPages}
                className="font-semibold text-blue-700 hover:text-blue-900 underline underline-offset-2"
              >
                {crossPageSelection.bannerLabel
                  ? crossPageSelection.bannerLabel(crossPageSelection.total)
                  : `Select all ${crossPageSelection.total}?`}
              </button>
            </div>
          )}

        {/* GH-1855: cross-page mode active — show total + clear affordance. */}
        {crossPageSelection?.enabled && selection.allSelectedAcrossPages && (
          <div className="flex items-center justify-center gap-2 border-b border-blue-200 bg-blue-100/70 px-4 py-2 text-[13px] text-slate-700">
            <span>
              All {Math.max(0, crossPageSelection.total - (selection.excludedIds?.length || 0))} across pages selected.
            </span>
            <button
              type="button"
              onClick={handleClearSelection}
              className="font-semibold text-blue-700 hover:text-blue-900 underline underline-offset-2"
            >
              Clear selection
            </button>
          </div>
        )}

        {/* Table */}
        <div
          ref={scrollContainerRef}
          // relative is load-bearing: absolutely-positioned descendants
          // (e.g. sr-only "Open menu" spans inside Dropdown triggers, or any
          // portal-less absolute child) would otherwise escape the scroll
          // container, find body as their offsetParent, and push the document
          // scrollWidth beyond the viewport — producing a page-level
          // horizontal scrollbar on tables with many columns.
          className={cn('relative overflow-x-auto max-w-full', HORIZONTAL_SCROLL_GUARD_CLASS)}
          onWheel={(e) => {
            // Allow vertical scrolling to pass through to parent when scrolling over table
            const isVerticalScroll = Math.abs(e.deltaY) > Math.abs(e.deltaX);
            const container = e.currentTarget;
            
            if (isVerticalScroll) {
              // Vertical scroll - find scrollable parent and scroll it
              let scrollableParent = container.parentElement;
              while (scrollableParent) {
                const style = window.getComputedStyle(scrollableParent);
                if (style.overflowY === 'auto' || style.overflowY === 'scroll' || 
                    scrollableParent.classList.contains('overflow-y-auto')) {
                  scrollableParent.scrollTop += e.deltaY;
                  e.preventDefault();
                  e.stopPropagation();
                  return;
                }
                scrollableParent = scrollableParent.parentElement;
              }
            } else {
              // Horizontal scroll - check if we're at boundaries
              const { scrollLeft, scrollWidth, clientWidth } = container;
              const atLeftEdge = scrollLeft === 0 && e.deltaX < 0;
              const atRightEdge = scrollLeft + clientWidth >= scrollWidth - 1 && e.deltaX > 0;
              
              // Only prevent default if we're NOT at edges (let natural scroll happen)
              // At edges, let it bubble up (but there's usually nowhere for it to go)
              if (!atLeftEdge && !atRightEdge) {
                // We're in the middle, let the container handle it
                // Don't call preventDefault - let natural horizontal scroll work
              }
            }
          }}
        >
          <table className={cn(layout === 'fixed' ? 'w-full' : 'min-w-full', tableLayoutClass)} aria-label={ariaLabel || strings.table} style={tableMinWidthStyle}>
            <thead className={cn(themeClasses.tableHeader, stickyHeader && 'sticky top-0 z-30')}>
              <tr>
                {showCheckbox && (
                  <th
                    data-dt-col="checkbox"
                    className={cn(
                      'dt-checkbox-col w-8 px-2 py-3 text-left align-middle',
                      bordered && 'border-r border-slate-200'
                    )}
                    style={{
                      // Inline width overrides percentage-based column
                      // distribution from sibling columns in table-layout:fixed,
                      // ensuring the checkbox column is always exactly 32px.
                      width: '32px',
                      position: 'sticky',
                      left: 0,
                      ...(stickyHeader && { top: 0 }),
                      zIndex: 35,
                      backgroundColor: 'var(--dt-bg-table-header)',
                    }}
                  >
                    {selectionMode === 'multiple' && (
                      <Checkbox
                        checked={selection.allSelected}
                        onCheckedChange={handleSelectAll}
                        aria-label={selection.allSelected ? strings.deselectAll : strings.selectAll}
                      />
                    )}
                  </th>
                )}
                {visibleColumns.map((column, colIndex) => {
                  const isSorted = sortState?.columnId === column.id;
                  const sortDirection = isSorted ? sortState.direction : null;
                  const isActionsColumn = column.id === 'actions' || column.type === 'actions';
                  // Only make Actions column sticky on the right, not other last columns
                  const isStickyActionColumn = stickyActions && isActionsColumn && hasHorizontalScroll;
                  const isStickyLeft = column.sticky === 'left';
                  const isLastColumn = colIndex === visibleColumns.length - 1;

                  return (
                    <th
                      key={column.id}
                      className={cn(
                        themeClasses.headerCell,
                        column.align === 'center' ? 'text-center' : column.align === 'right' ? 'text-right' : 'text-left',
                        isStickyActionColumn && 'border-l border-slate-200',
                        isStickyLeft && 'border-r border-slate-200',
                        bordered && !isLastColumn && !isStickyActionColumn && !isStickyLeft && 'border-r border-slate-200'
                      )}
                      style={{
                        width: column.width,
                        minWidth: column.minWidth,
                        maxWidth: column.maxWidth,
                        // Sticky header cells need `top: 0` here too — applying
                        // `position: sticky` directly to a <th> (for left/right
                        // pinning) overrides the thead's sticky-top behavior for
                        // that cell, so we must reassert the vertical pin.
                        ...(isStickyActionColumn && {
                          position: 'sticky',
                          right: 0,
                          ...(stickyHeader && { top: 0 }),
                          zIndex: 25,
                          backgroundColor: 'var(--dt-bg-table-header)',
                          // Solid-color extension masks sub-pixel rendering
                          // gaps where the scrolling neighbor's content can
                          // bleed through under `table-auto` layout.
                          boxShadow:
                            '-4px 0 0 0 var(--dt-bg-table-header), -2px 0 8px rgba(0, 0, 0, 0.12)',
                        }),
                        ...(isStickyLeft && {
                          position: 'sticky',
                          // Offset by the pinned checkbox column width (if
                          // selection is enabled) so both sticky columns sit
                          // side-by-side instead of colliding at left: 0.
                          // Checkbox cell is w-8 + px-2 = 32px (border-box).
                          left: showCheckbox ? 32 : 0,
                          ...(stickyHeader && { top: 0 }),
                          zIndex: 25,
                          backgroundColor: 'var(--dt-bg-table-header)',
                          boxShadow:
                            '4px 0 0 0 var(--dt-bg-table-header), 2px 0 8px rgba(0, 0, 0, 0.12)',
                        }),
                        ...textStyles.header(),
                      }}
                      aria-sort={
                        column.sortable && isSorted
                          ? sortDirection === 'asc'
                            ? 'ascending'
                            : 'descending'
                          : undefined
                      }
                    >
                      {renderHeaderCell(column)}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody className="bg-white">
              {processedData.map((row, rowIndex) => {
                const rowKeyValue = getRowKey(row, rowIndex);
                const isSelected = selection.selectedIndices.includes(rowIndex);
                const isExpanded = expandedRowKeys.has(rowKeyValue);

                return (
                  <React.Fragment key={rowKeyValue}>
                    {/* Main Row */}
                    <tr
                      className={cn(
                        rowClass,
                        isSelected && 'bg-[var(--dt-bg-row-selected)]',
                        (callbacks?.onRowClick || expandable) && 'cursor-pointer',
                        !(callbacks?.onRowClick || expandable) && 'cursor-default',
                        isExpanded && 'bg-white',
                        getRowClass?.(row)
                      )}
                      style={
                        !(callbacks?.onRowClick || expandable)
                          ? { cursor: 'default' }
                          : undefined
                      }
                      onClick={
                        callbacks?.onRowClick || expandable
                          ? () => {
                              // If expandable is configured, clicking row toggles expand
                              if (expandable) {
                                handleToggleExpand(rowKeyValue);
                              }
                              // Also call onRowClick callback if provided
                              callbacks?.onRowClick?.(row, rowIndex);
                            }
                          : undefined
                      }
                    >
                      {showCheckbox && (
                        <td
                          data-dt-col="checkbox"
                          className={cn(
                            'dt-checkbox-col w-8 px-2 py-4',
                            bordered && 'border-r border-slate-200',
                            bordered && rowIndex !== processedData.length - 1 && 'border-b border-slate-200'
                          )}
                          style={{
                            width: '32px',
                            position: 'sticky',
                            left: 0,
                            zIndex: 20,
                            backgroundColor: isSelected
                              ? 'var(--dt-bg-row-selected)'
                              : isExpanded
                                ? '#FCFCFC'
                                : 'var(--dt-bg-row)',
                          }}
                        >
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={() => handleSelectRow(rowIndex)}
                            aria-label={`${strings.selectRow} ${rowIndex + 1}`}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </td>
                      )}
                {visibleColumns.map((column, colIdx) => {
                        const isActionsColumn = column.id === 'actions' || column.type === 'actions';
                        // Only make Actions column sticky on the right, not other last columns
                        const isStickyRight = stickyActions && isActionsColumn && hasHorizontalScroll;
                        const isStickyLeft = column.sticky === 'left';
                        const isLastColumn = colIdx === visibleColumns.length - 1;
                        const isLastRow = rowIndex === processedData.length - 1;

                        return (
                          <TableCell
                            key={column.id}
                            row={row}
                            rowIndex={rowIndex}
                            column={column}
                            theme={theme}
                            onClick={callbacks?.onCellClick ? () => callbacks?.onCellClick?.(row, column, rowIndex) : undefined}
                            isExpanded={isExpanded}
                            onToggleExpand={() => handleToggleExpand(rowKeyValue)}
                            isStickyRight={isStickyRight}
                            isStickyLeft={isStickyLeft}
                            isSelected={isSelected}
                            hasLeftCheckboxOffset={showCheckbox}
                            bordered={bordered}
                            isLastRow={isLastRow}
                            isLastColumn={isLastColumn}
                          />
                        );
                      })}
                    </tr>

                    {/* Expanded Row Content */}
                    {isExpanded && expandable && (
                      <tr className="border-b border-slate-200">
                        <td
                          colSpan={columnCount}
                          className="p-0"
                        >
                          {expandable.renderExpandedRow(row, rowIndex)}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * DataTable with theme provider wrapper
 */
export function DataTable<TData = any>(props: DataTableProps<TData>) {
  return (
    <DataTableThemeProvider theme={props.theme}>
      <DataTableInner {...props} />
    </DataTableThemeProvider>
  );
}

// Export types and utilities for consumers
export * from './types';
;
;

// Export helper components for custom renderCell functions
export { CellText } from './cells/CellText';
;

// Platform-wide Pill component (from components/ui/)
// Re-exported for convenience - can also import from '@/components/ui/pill'
export { Pill } from '@/components/ui/pill';

// Backward compatibility alias (deprecated - use Pill instead)
;
